import { useSelector } from 'react-redux';
import {
  computeBreadcrumbSegments, filterAttachments, sortAttachments,
} from '@/datasets/attachments/helper';
import { newRequest, requestActionsState, resolveRequestWorkflow } from '../../state/state';

const computeAttachmentsBreadcrumb = ({ steps: { attachments } }) => {
  const state = computeBreadcrumbSegments({ messageId: 'dataset.attachments' }, attachments.folders);

  return {
    ...state,
    readStatus: state.isRoot ? attachments.readStatus : attachments.navigation.readStatus,
    readErrors: state.isRoot ? attachments.readErrors : attachments.navigation.readErrors,
  };
};

const requestAttachmentsState = (category, code, requests, workflows) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, newRequest({ category, code }));
  const state = computeAttachmentsBreadcrumb(request);
  const {
    filters, orderBy, results, navigation, folders,
  } = request.steps.attachments;
  const items = state.isRoot ? results : navigation.results;
  const directories = state.isRoot ? undefined : folders;
  const attachments = sortAttachments(filterAttachments(items, filters, directories, false), orderBy);

  return { ...state, attachments };
};

const useRequestAttachmentListItem = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, { attachments: [] });
  return { attachments: request.attachments || [] };
});

const useRequestAttachmentsCards = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => requestAttachmentsState(category, code, requests, workflows));

const useRequestAttachmentsTable = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => requestAttachmentsState(category, code, requests, workflows));

const useRequestAttachmentsFilters = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, newRequest({ category, code }));
  const { byTextInput: filterTerm, byType: typeFilter } = request.steps.attachments.filters;
  return { filterTerm, typeFilter };
});

const useRequestAttachmentsTableHeader = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, newRequest({ category, code }));
  const {
    isRoot, segments, readStatus, readErrors,
  } = computeAttachmentsBreadcrumb(request);

  return {
    isRoot,
    readErrors,
    readStatus,
    segments,
  };
});

const useRequestAttachmentsTableColumns = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, newRequest({ category, code }));
  return { isRoot: computeAttachmentsBreadcrumb(request).isRoot, orderBy: request.steps.attachments.orderBy };
});

const useSelectAttachmentsActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { locations, forms },
}) => {
  const { steps, readErrors, saveStatus } = requestActionsState(
    { category, code },
    requests,
    workflows,
    locations,
    forms,
  );
  const { attachments, sourceUrl } = steps;

  return {
    attachments,
    readErrors,
    saveStatus,
    sourceUrl,
  };
});

export default {
  useRequestAttachmentListItem,
  useRequestAttachmentsCards,
  useRequestAttachmentsFilters,
  useRequestAttachmentsTable,
  useRequestAttachmentsTableColumns,
  useRequestAttachmentsTableHeader,
  useSelectAttachmentsActions,
};
